import { memo, useEffect, useState, useRef, ChangeEvent } from "react";
import { Grid2, Typography, Alert, Button, Dialog, DialogTitle, DialogContent, DialogActions, Box, CircularProgress } from "@mui/material";
import { useMutation, useQuery } from "@apollo/client";
import { useLocation, useNavigate } from "react-router-dom";
import { FETCH_COMPANY } from "@company/graphql/queries/fetchCompany";
import { Mutation, MutationUpdateCompanyArgs, Query } from "@/app/graphql/codegen";
import { UPDATE_COMPANY } from "@company/graphql/mutations/updateCompany";
import BaseSettingsPage from "@settings/base/BaseSettingsPage";
import { useDispatch } from "react-redux";
import { handleErrorWithNotification, showSuccessNotification } from "@/utils/errorHandling";
import { UPDATE_COMPANY_PROFILE } from "@company/graphql/mutations/updateCompanyProfile";
import SettingsCard from "@settings/base/SettingsCard";
import { CompanyBasicInfoCard } from "@settings/components/cards/CompanyBasicInfoCard";
import axios from "axios";
import { LocalStorage } from "@/types/enums";
import { UploadedDocument } from "@/types/document";
import { FETCH_COMPANY_UPLOADS } from "@company/graphql/queries/fetchCompanyUploads";
import { DELETE_COMPANY_UPLOAD } from "@company/graphql/mutations/deleteCompanyUpload";
import { GENERATE_COMPANY_PROFILE_FROM_UPLOADS } from "@company/graphql/mutations/generateCompanyProfileFromUploads";
import { FETCH_USER_PROFILE } from "@company/graphql/queries/fetchUserProfile";
import { SettingsFileList } from "@settings/components/forms/SettingsFileList";
import { useEnterKeyDialog } from "@core/hooks/useEnterKeyDialog";
import AVVCreation, { AVVState } from "@company/components/AVVCreation";

// Constants for cost calculation
const TOKENS_PER_PAGE = 1000;
const COST_PER_PAGE = 0.05; // €0.05 per page

// Define the possible modes for the page
enum ProfilePageMode {
    VIEW = "view",
    EDIT = "edit",
    GENERATING = "generating",
}

const ConsolidatedCompanyProfilePage = memo(() => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const { data, loading, refetch } = useQuery<Pick<Query, "company">>(FETCH_COMPANY);
    const company = data?.company;
    const companyId = company?.id || "";

    // Main state for determining which mode the page is in
    const [mode, setMode] = useState<ProfilePageMode>(ProfilePageMode.VIEW);

    // State for tracking manual generation initiation (prevents race conditions)
    const [isLocallyGenerating, setIsLocallyGenerating] = useState(false);
    const [generationStartTime, setGenerationStartTime] = useState<string | null>(null);
    const [generationError, setGenerationError] = useState<string | null>(null);

    // State for profile editing
    const [generatedProfile, setGeneratedProfile] = useState("");
    const [hasProfileChanges, setHasProfileChanges] = useState(false);

    // State for page quota tracking
    const [pageQuotaLeft, setPageQuotaLeft] = useState<number>(0);
    const [totalPageQuota, setTotalPageQuota] = useState<number>(0);
    const [lastGenerationCost, setLastGenerationCost] = useState<number | null>(null);

    // State for company info editing
    type FormDataType = {
        name: string;
        employeeCount: string;
        locations: string[];
    };

    const [formData, setFormData] = useState<FormDataType>({
        name: "",
        employeeCount: "",
        locations: [],
    });

    // Stored previous values to allow restoration after clearing fields
    const [previousValues, setPreviousValues] = useState<{ formData: FormDataType; description: string; websites: string[] } | null>(null);
    const [description, setDescription] = useState("");
    const [websites, setWebsites] = useState<string[]>([]);
    const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
    const [avvState, setAVVState] = useState<AVVState>({ status: "not_created" });

    // State for file upload and dialogs
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

    // Reference to track if notification has been shown
    const notificationShownRef = useRef(false);

    // Fetch user profile to get page quota
    const {
        data: userData,
        loading: userLoading,
        refetch: refetchUser,
    } = useQuery<Pick<Query, "userProfile">>(FETCH_USER_PROFILE, {
        fetchPolicy: "network-only",
        onCompleted: (data) => {
            setPageQuotaLeft((data?.userProfile?.pageQuota || 0) - (data?.userProfile?.usedPages || 0));
            setTotalPageQuota(data?.userProfile?.pageQuota || 0);
        },
    });

    // Fetch uploaded documents
    const {
        data: documentsData,
        loading: documentsLoading,
        refetch: refetchDocuments,
    } = useQuery<{ companyUploads: UploadedDocument[] }>(FETCH_COMPANY_UPLOADS, {
        variables: { companyId },
        skip: !companyId,
    });

    const documents = documentsData?.companyUploads || [];

    // Mutations
    const [deleteDocument, { loading: deleting }] = useMutation(DELETE_COMPANY_UPLOAD);
    const [generateProfile, { loading: generatingMutation }] = useMutation(GENERATE_COMPANY_PROFILE_FROM_UPLOADS);
    const [updateCompanyMutation, { loading: updating }] = useMutation<Pick<Mutation, "updateCompany">, MutationUpdateCompanyArgs>(UPDATE_COMPANY);
    const [updateCompanyProfile, { loading: updatingProfile }] = useMutation(UPDATE_COMPANY_PROFILE);

    // Determine if profile generation is in progress
    const isGeneratingProfile = (company?.details as any)?.isGeneratingProfile || false;

    // Combined loading states
    const isLoading = loading || documentsLoading || uploading || deleting || updating || updatingProfile;
    const generating = generatingMutation || isGeneratingProfile;

    // Show success notification if passed in navigation state
    useEffect(() => {
        // Check if there's a success message in the location state
        const { state } = location;
        if (state?.successMessage && !notificationShownRef.current) {
            // Show the notification
            showSuccessNotification(state.successMessage, dispatch);

            // Mark notification as shown
            notificationShownRef.current = true;

            // Clear the state by replacing the current history entry
            navigate(location.pathname, {
                replace: true,
                state: {},
            });
        }
    }, [location, dispatch, navigate]);

    // Initialize data from company
    useEffect(() => {
        if (company) {
            // Set generated profile
            if (company.details?.generatedProfile) {
                setGeneratedProfile(company.details.generatedProfile);
            }

            // Set company form data
            const metadata = company.details?.metadata as { locations?: string[]; websites?: string[], lastGenerationError?: { message: string } };
            setFormData({
                name: company.name || "",
                employeeCount: company.details?.employeeCount?.toString() || "",
                locations: metadata?.locations || [],
            });
            setDescription(company.details?.description || "");
            setWebsites(metadata?.websites || []);

            // If there is a stored generation error, surface it
            if (metadata?.lastGenerationError && !isGeneratingProfile) {
                setGenerationError(metadata.lastGenerationError.message);
            }
        }
    }, [company]);

    // Determine the initial mode based on company state
    useEffect(() => {
        if (loading) return;

        // Don't override mode if we're locally generating (prevents race condition)
        if (isLocallyGenerating) return;

        if (isGeneratingProfile) {
            setMode(ProfilePageMode.GENERATING);
        } else if (!company?.details?.generatedProfile) {
            setMode(ProfilePageMode.EDIT);
        } else {
            setMode(ProfilePageMode.VIEW);
        }
    }, [company, loading, isGeneratingProfile, isLocallyGenerating]);

    // Poll for status changes when in generating mode
    useEffect(() => {
        if (mode !== ProfilePageMode.GENERATING) return;

        let pollCount = 0;
        const maxPollInterval = 15000; // Max 15 seconds between polls
        
        const pollWithBackoff = () => {
            // For the first poll, wait a bit longer to give backend time to start processing
            // Exponential backoff: 5s, 7s, 10s, 14s, 15s, 15s...
            const baseInterval = pollCount === 0 ? 5000 : Math.min(3000 + pollCount * 2000, maxPollInterval);
            
            const timeoutId = setTimeout(async () => {
                try {
                    await refetch();
                    await refetchUser();

                    // Give a small delay after refetch to ensure data is updated
                    await new Promise(resolve => setTimeout(resolve, 100));

                    // Debug logging
                    const metadata = company?.details?.metadata as any;
                    const lastError = metadata?.lastGenerationError;
                    console.log(`Polling check ${pollCount + 1}: isGeneratingProfile=${isGeneratingProfile}, hasProfile=${!!company?.details?.generatedProfile}, hasError=${!!lastError}, errorTime=${lastError?.timestamp}, startTime=${generationStartTime}`);

                    // Check if generation is no longer in progress
                    if (!isGeneratingProfile) {
                        // Check for errors in metadata
                        const metadata = company?.details?.metadata as any;
                        const lastError = metadata?.lastGenerationError;
                        
                        // Check if there's an error from this generation session
                        if (lastError && generationStartTime && lastError.timestamp > generationStartTime) {
                            // Error occurred during this generation
                            setGenerationError(lastError.message);
                            setMode(ProfilePageMode.EDIT);
                            setIsLocallyGenerating(false);
                        } else if (lastError && generationStartTime && lastError.timestamp >= generationStartTime) {
                            // Error timestamp matches generation start (same second) - also treat as error
                            setGenerationError(lastError.message);
                            setMode(ProfilePageMode.EDIT);
                            setIsLocallyGenerating(false);
                        } else if (company?.details?.generatedProfile) {
                            // Only show success if NO recent error AND we have a profile
                            // Additional check: make sure no error exists within 10 seconds of generation start
                            const generationStartMs = new Date(generationStartTime || '').getTime();
                            const errorMs = lastError ? new Date(lastError.timestamp).getTime() : 0;
                            const timeDifference = Math.abs(errorMs - generationStartMs);
                            
                            if (lastError && timeDifference < 10000) {
                                // Error occurred close to generation start - treat as generation error
                                setGenerationError(lastError.message);
                                setMode(ProfilePageMode.EDIT);
                                setIsLocallyGenerating(false);
                            } else {
                                // Truly successful completion
                                setGenerationError(null);
                                setMode(ProfilePageMode.VIEW);
                                setIsLocallyGenerating(false);
                                showSuccessNotification("Firmenprofil wurde erfolgreich generiert", dispatch);
                            }
                        } else {
                            // Generation stopped but no profile - something went wrong
                            setGenerationError("Unbekannter Fehler bei der Generierung");
                            setMode(ProfilePageMode.EDIT);
                            setIsLocallyGenerating(false);
                        }
                    } else {
                        // Still generating, continue polling with backoff
                        pollCount++;
                        pollWithBackoff();
                    }
                } catch (error) {
                    console.error('Error during polling:', error);
                    // Continue polling on network errors
                    pollCount++;
                    pollWithBackoff();
                }
            }, baseInterval);

            return timeoutId;
        };

        const timeoutId = pollWithBackoff();

        return () => clearTimeout(timeoutId);
    }, [mode, company, refetch, refetchUser, isGeneratingProfile, dispatch, generationStartTime]);

    // Check if profile has been edited
    useEffect(() => {
        if (company) {
            setHasProfileChanges(generatedProfile !== (company.details?.generatedProfile || ""));
        }
    }, [generatedProfile, company]);

    // Validation function for the form
    const validateForm = () => {
        const errors: Record<string, string> = {};

        // Optional: validate name only if provided
        if (formData.name && formData.name.length < 3) {
            errors.name = "Name muss mindestens 3 Zeichen lang sein";
        }
        if (formData.name && formData.name.length > 100) {
            errors.name = "Name darf maximal 100 Zeichen lang sein";
        }

        // Optional: validate employee count only if provided
        if (formData.employeeCount) {
            const parsed = parseInt(formData.employeeCount);
            if (isNaN(parsed)) {
                errors.employeeCount = "Mitarbeiteranzahl muss eine Zahl sein";
            } else if (parsed < 0) {
                errors.employeeCount = "Mitarbeiteranzahl muss eine positive Zahl sein";
            }
        }

        // Set validation errors
        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Save changes to company info
    const handleSaveCompany = async () => {
        try {
            // Validate form data
            if (!validateForm()) return false;

            // Update company mutation
            await updateCompanyMutation({
                variables: {
                    name: formData.name || undefined,
                    employeeCount: formData.employeeCount ? parseInt(formData.employeeCount) : undefined,
                    locations: formData.locations.length ? formData.locations : undefined,
                    description: description || undefined,
                    websites: websites.length ? websites : undefined,
                },
                refetchQueries: [{ query: FETCH_COMPANY }],
            });
            return true;
        } catch (error) {
            // Error handling
            handleErrorWithNotification(error as Error, dispatch, "Fehler", "Die Unternehmenseinstellungen konnten nicht gespeichert werden");
            return false;
        }
    };

    // Save changes to the generated profile
    const handleSaveProfile = async () => {
        try {
            await updateCompanyProfile({
                variables: {
                    profile: generatedProfile,
                },
                refetchQueries: [{ query: FETCH_COMPANY }],
            });
            return true;
        } catch (error) {
            handleErrorWithNotification(error as Error, dispatch, "Fehler", "Das generierte Firmenprofil konnte nicht gespeichert werden");
            return false;
        }
    };

    // Location handlers
    const handleAddLocation = (location: string) => {
        setFormData((prev) => {
            if (prev.locations.includes(location)) {
                return prev;
            }
            return {
                ...prev,
                locations: [...prev.locations, location],
            };
        });
    };

    const handleRemoveLocation = (location: string) => {
        setFormData((prev) => ({
            ...prev,
            locations: prev.locations.filter((l) => l !== location),
        }));
    };

    // Website handlers
    const handleAddWebsite = (website: string) => {
        if (websites.includes(website)) return;
        setWebsites([...websites, website]);
    };

    const handleRemoveWebsite = (website: string) => {
        setWebsites(websites.filter((w) => w !== website));
    };

    // Start new company profile generation - clears fields and switches to edit mode
    const handleStartNewGeneration = () => {
        // Save current values to allow restoring
        setPreviousValues({
            formData,
            description,
            websites,
        });
        // Clear current input fields
        setFormData({ name: "", employeeCount: "", locations: [] });
        setDescription("");
        setWebsites([]);
        setMode(ProfilePageMode.EDIT);
        setGenerationError(null); // Clear any previous errors
    };

    // Handle file selection
    const handleFileSelect = (event: ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files || files.length === 0) return;

        // Create form data with selected files
        const formData = new FormData();
        for (let i = 0; i < files.length; i++) {
            if (files[i].type === "application/pdf") {
                formData.append("documents", files[i]);
            } else {
                setUploadError("Nur PDF-Dateien sind erlaubt.");
                return;
            }
        }

        uploadFiles(formData);
    };

    // Upload files to server
    const uploadFiles = async (formData: FormData) => {
        if (!companyId) {
            setUploadError("Keine Firma ausgewählt.");
            return;
        }

        setUploading(true);
        setUploadError(null);

        try {
            // Get authentication token from local storage
            const authToken = localStorage.getItem(LocalStorage.AUTH_TOKEN);

            // Make sure we have a valid token
            if (!authToken) {
                setUploadError("Nicht authentifiziert. Bitte melden Sie sich erneut an.");
                return;
            }

            // Format token for request
            const formattedToken = `Bearer ${authToken}`;

            const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL || "http://localhost:4000"}/upload/${companyId}`, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                    Authorization: formattedToken,
                },
            });

            if (response.data.success) {
                showSuccessNotification("Dateien wurden erfolgreich hochgeladen", dispatch);
                refetchDocuments();
            } else {
                setUploadError("Upload fehlgeschlagen: " + response.data.error);
            }
        } catch (error) {
            console.error("Upload error:", error);
            setUploadError("Upload fehlgeschlagen. Bitte versuchen Sie es erneut.");
            handleErrorWithNotification(error as Error, dispatch, "Upload Fehler", "Die Dateien konnten nicht hochgeladen werden");
        } finally {
            setUploading(false);
            // Clear the file input
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        }
    };

    // Handle document deletion
    const handleDeleteDocument = async (filename: string) => {
        try {
            await deleteDocument({
                variables: {
                    companyId,
                    filename,
                },
            });
            refetchDocuments();
            showSuccessNotification("Dokument wurde gelöscht", dispatch);
        } catch (error) {
            handleErrorWithNotification(error as Error, dispatch, "Fehler", "Das Dokument konnte nicht gelöscht werden");
        }
    };

    // Handle profile generation
    const handleGenerateProfile = async () => {
        // Open confirmation dialog
        setConfirmDialogOpen(true);
    };

    // Handle confirmation dialog confirm action
    const handleConfirmGeneration = async () => {
        setConfirmDialogOpen(false);

        try {
            // Set local flag first to prevent race condition
            setIsLocallyGenerating(true);
            // Clear previous errors and set start time
            setGenerationError(null);
            setGenerationStartTime(new Date().toISOString());
            // Switch to generating mode
            setMode(ProfilePageMode.GENERATING);

            // First save the company information
            const success = await handleSaveCompany();
            if (!success) {
                setIsLocallyGenerating(false); // Reset flag on failure
                setGenerationStartTime(null);
                return; // Don't proceed if saving failed
            }

            // Call the mutation with useExistingProfile explicitly set to false
            await generateProfile({
                variables: {
                    companyId,
                    useExistingProfile: false,
                },
                refetchQueries: [{ query: FETCH_COMPANY }],
            });
        } catch (error) {
            setIsLocallyGenerating(false); // Reset flag on error
            setGenerationStartTime(null);
            handleErrorWithNotification(error as Error, dispatch, "Fehler", "Die Profilgenerierung konnte nicht gestartet werden");
        }
    };

    // Handle confirmation dialog cancel action
    const handleCancelGeneration = () => {
        setConfirmDialogOpen(false);
    };

    // Enter key support for confirmation dialog
    useEnterKeyDialog({
        isOpen: confirmDialogOpen,
        onConfirm: handleConfirmGeneration,
        disabled: generating || (documents.length === 0 && description.trim() === "") || deleting || uploading,
    });

    // Estimate the token costs for display in the confirmation dialog
    const estimatedTokens = documents.reduce((sum, doc) => sum + (doc.tokenCount || 0), 0);
    const estimatedPages = Math.ceil(estimatedTokens / TOKENS_PER_PAGE);

    // Render VIEW mode content
    const renderViewMode = () => {
        return (
            <Grid2 container spacing={3}>
                {/* Generated Profile */}
                <Grid2 sx={{ xs: 12, width: "100%" }}>
                    <SettingsCard title="Generiertes Firmenprofil">
                        {!company?.details?.generatedProfile ? (
                            <Alert severity="info" sx={{ mb: 2 }}>
                                Es wurde noch kein Firmenprofil generiert. Klicken Sie auf "Neues Firmenprofil generieren", um ein Profil zu erstellen.
                            </Alert>
                        ) : (
                            <Typography variant="body2" color="textSecondary" paragraph>
                                Hier können Sie das generierte Firmenprofil bearbeiten.
                            </Typography>
                        )}

                        <textarea
                            value={generatedProfile}
                            onChange={(e) => setGeneratedProfile(e.target.value)}
                            style={{
                                width: "100%",
                                minHeight: "500px",
                                padding: "16px",
                                borderRadius: "8px",
                                border: "1px solid #ccc",
                                fontFamily: "inherit",
                                fontSize: "16px",
                                lineHeight: "1.5",
                                resize: "vertical",
                            }}
                            disabled={isLoading}
                            placeholder="Hier erscheint das generierte Firmenprofil..."
                        />

                        <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
                            <Button variant="contained" color="primary" onClick={() => {
                                handleStartNewGeneration();
                            }} sx={{ mr: 2 }}>
                                Neues Firmenprofil generieren
                            </Button>
                        </Box>
                    </SettingsCard>
                </Grid2>
            </Grid2>
        );
    };

    // Render EDIT mode content
    const renderEditMode = () => {
        return (
            <Grid2 container spacing={3}>
                {previousValues && (
                    <Grid2 sx={{ xs: 12, width: "100%" }}>
                        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
                            <Button variant="outlined" onClick={() => {
                                setFormData(previousValues.formData);
                                setDescription(previousValues.description);
                                setWebsites(previousValues.websites);
                            }}>
                                Vorherige Werte wiederherstellen
                            </Button>
                        </Box>
                    </Grid2>
                )}
                {/* Company Basic Info */}
                <Grid2 sx={{ xs: 12, width: "100%" }}>
                    <SettingsCard title="Firmeninformationen">
                        <Grid2 container spacing={3} sx={{ width: "100%" }}>
                            <Grid2 sx={{ xs: 12, width: "100%" }}>
                                <CompanyBasicInfoCard
                                    name={formData.name}
                                    employeeCount={formData.employeeCount}
                                    onNameChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                                    onEmployeeCountChange={(e) => setFormData((prev) => ({ ...prev, employeeCount: e.target.value }))}
                                    nameError={validationErrors.name}
                                    employeeCountError={validationErrors.employeeCount}
                                    loading={isLoading}
                                    // Locations
                                    locations={formData.locations}
                                    onAddLocation={handleAddLocation}
                                    onRemoveLocation={handleRemoveLocation}
                                    // Websites
                                    websites={websites}
                                    onAddWebsite={(website) => {
                                        // Basic validation before adding
                                        const regex = /^(https?:\/\/)?(www\.)?[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(\/\S*)?$/;
                                        if (regex.test(website)) {
                                            handleAddWebsite(website);
                                        } else {
                                            // Could show an error in UI, but for now just don't add invalid websites
                                            showSuccessNotification("Bitte geben Sie eine gültige URL ein", dispatch, "error");
                                        }
                                    }}
                                    onRemoveWebsite={handleRemoveWebsite}
                                />
                            </Grid2>
                        </Grid2>
                    </SettingsCard>
                </Grid2>

                {/* Company Description */}
                <Grid2 sx={{ xs: 12, width: "100%" }}>
                    <SettingsCard title="Firmenbeschreibung">
                        <Typography variant="body2" color="textSecondary" paragraph>
                            Bitte beschreiben Sie Ihre Firma so präzise und ausführlich wie möglich. Je detaillierter die Angaben, desto besser kann der ESGbot Ihr
                            Firmenprofil generieren. Dieses Profil dient als Grundlage für KI-basierte Analysen und kann im Chat gezielt verwendet werden.
                        </Typography>

                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            style={{
                                width: "100%",
                                minHeight: "250px",
                                padding: "16px",
                                borderRadius: "8px",
                                border: "1px solid #ccc",
                                fontFamily: "inherit",
                                fontSize: "16px",
                                lineHeight: "1.5",
                                resize: "vertical",
                            }}
                            disabled={isLoading}
                            placeholder="Beschreiben Sie Ihre Firma hier..."
                        />

                        <Grid2 container size={12} spacing={1.5} pt={2}>
                            {/* Document List */}
                            <SettingsFileList
                                files={documents}
                                onDelete={handleDeleteDocument}
                                onUpload={handleFileSelect}
                                loading={documentsLoading}
                                uploadingInProgress={uploading || deleting}
                                tokensPerPage={TOKENS_PER_PAGE}
                                label="Hochgeladene Dokumente"
                                error={uploadError || undefined}
                            />

                            {/* Generation Button and Info */}
                            <Grid2 container sx={{ xs: 12, width: "100%" }}>
                                <Grid2 container direction="column" alignItems="flex-end" sx={{ xs: 12, width: "100%" }}>
                                    <Grid2 sx={{ xs: 12, width: "100%" }}>
                                        <Typography variant="body2" color="textSecondary" align="right">
                                            Verbleibendes Kontingent: {pageQuotaLeft} von {totalPageQuota} Seiten
                                        </Typography>
                                    </Grid2>

                                    {pageQuotaLeft < estimatedPages && documents.length > 0 && !generating && (
                                        <Grid2 sx={{ xs: 12, width: "100%" }}>
                                            <Alert severity="warning">
                                                Warnung: Die geschätzten Kosten ({estimatedPages} Seiten) überschreiten Ihr verbleibendes Kontingent ({pageQuotaLeft} Seiten).
                                            </Alert>
                                        </Grid2>
                                    )}

                                    <Grid2 container justifyContent="flex-end" sx={{ xs: 12, width: "100%" }}>
                                        {company?.details?.generatedProfile && (
                                            <Button variant="outlined" onClick={() => {
                                                setMode(ProfilePageMode.VIEW);
                                                setGenerationError(null); // Clear any errors when canceling
                                            }} sx={{ mr: 2 }}>
                                                Generierung Abbrechen
                                            </Button>
                                        )}
                                        <Button
                                            variant="contained"
                                            color="secondary"
                                            onClick={handleGenerateProfile}
                                            disabled={generating || (documents.length === 0 && description.trim() === "") || deleting || uploading}
                                            sx={{ minWidth: 200 }}
                                        >
                                            Firmenprofil generieren
                                        </Button>
                                    </Grid2>
                                </Grid2>
                            </Grid2>

                            {/* Generation Confirmation Dialog */}
                            <Dialog open={confirmDialogOpen} onClose={handleCancelGeneration}>
                                <DialogTitle>Firmenprofil generieren?</DialogTitle>
                                <DialogContent>
                                    <Typography gutterBottom>
                                        Sie sind dabei, ein Firmenprofil zu erstellen.
                                        {documents.length > 0
                                            ? " Die hochgeladenen Dokumente werden für eine KI-Analyse verwendet. Dieser Vorgang kann je nach Länge der Dokumente einige Minuten dauern."
                                            : " Da keine Dokumente hochgeladen wurden, wird das Profil nur basierend auf den eingegebenen Firmeninformationen und der Beschreibung erstellt. Dieser Vorgang ist sehr schnell und kostet keine Seiten."}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        {documents.length > 0 ? <>Geschätzte Kosten: {estimatedPages} Seiten</> : <>Keine Kosten (es werden keine Dokumente analysiert).</>}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        Die eingegebenen Firmeninformationen (Name, Beschreibung, Standorte, etc.) werden für die Erstellung des Firmenprofils verwendet.
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        Ihr verbleibendes Kontingent: {pageQuotaLeft} Seiten
                                    </Typography>
                                    {documents.length > 0 && estimatedPages > pageQuotaLeft && (
                                        <Alert severity="warning" sx={{ mt: 2 }}>
                                            Die geschätzten Kosten überschreiten Ihr verbleibendes Kontingent. Der Vorgang wird möglicherweise abgebrochen!
                                        </Alert>
                                    )}
                                </DialogContent>
                                <DialogActions>
                                    <Button onClick={handleCancelGeneration}>Abbrechen</Button>
                                    <Button onClick={handleConfirmGeneration} color="primary" variant="contained">
                                        Generieren
                                    </Button>
                                </DialogActions>
                            </Dialog>
                        </Grid2>
                    </SettingsCard>
                </Grid2>
            </Grid2>
        );
    };

    // Render GENERATING mode content
    const renderGeneratingMode = () => {
        return (
            <Grid2 container spacing={3}>
                <Grid2 sx={{ xs: 12, width: "100%" }}>
                    <SettingsCard title="Firmenprofil wird generiert">
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 5 }}>
                            <CircularProgress size={60} sx={{ mb: 3 }} />
                            <Typography variant="h6" align="center" gutterBottom>
                                Ihr Firmenprofil wird generiert...
                            </Typography>
                            <Typography variant="body1" align="center" color="textSecondary" paragraph>
                                Dieser Vorgang kann je nach Menge und Umfang der Dokumente einige Minuten dauern.
                            </Typography>
                            <Typography variant="body2" align="center" color="textSecondary">
                                Du wirst automatisch weitergeleitet, sobald der Vorgang abgeschlossen ist.
                            </Typography>
                        </Box>
                    </SettingsCard>
                </Grid2>
            </Grid2>
        );
    };

    // Render content based on current mode
    const renderContent = () => {
        switch (mode) {
            case ProfilePageMode.VIEW:
                return renderViewMode();
            case ProfilePageMode.EDIT:
                return renderEditMode();
            case ProfilePageMode.GENERATING:
                return renderGeneratingMode();
            default:
                return renderViewMode();
        }
    };

    // Determine page title and save function based on mode
    let pageTitle = "Firmenprofil";
    let saveFunction: (() => Promise<boolean>) | undefined = undefined;
    let saveDisabled = false;

    if (mode === ProfilePageMode.VIEW && hasProfileChanges) {
        saveFunction = handleSaveProfile;
        saveDisabled = isLoading;
    }

    return (
        <BaseSettingsPage
            title={pageTitle}
            onSave={saveFunction}
            backButton={mode === ProfilePageMode.EDIT}
            onBackButtonClick={() => setMode(ProfilePageMode.VIEW)}
            explicitBackButtonTitle={mode === ProfilePageMode.EDIT && company?.details?.generatedProfile ? "Zurück zum Profil" : undefined}
            saveDisabled={saveDisabled}
            successMessage="Das Firmenprofil wurde erfolgreich gespeichert"
            hasChanges={hasProfileChanges}
            useOuterScroll
        >
            {Object.keys(validationErrors).length > 0 && mode === ProfilePageMode.EDIT && (
                <Grid2 mb={2}>
                    <Alert severity="error">
                        <Typography variant="subtitle2" color="textSecondary" fontSize={16}>
                            Bitte überprüfen Sie Ihre Eingaben:
                        </Typography>
                        {Object.entries(validationErrors).map(([key, error]) => (
                            <Typography key={key} fontSize={15}>
                                {error}
                            </Typography>
                        ))}
                    </Alert>
                </Grid2>
            )}

            {generationError && mode !== ProfilePageMode.GENERATING && (
                <Grid2 mb={2}>
                    <Alert severity="error">
                        <Typography variant="subtitle2" color="textSecondary" fontSize={16}>
                            Fehler bei der Profilgenerierung:
                        </Typography>
                        <Typography fontSize={15}>
                            {generationError}
                        </Typography>
                    </Alert>
                </Grid2>
            )}

            {renderContent()}
        </BaseSettingsPage>
    );
});

export default ConsolidatedCompanyProfilePage;
